import React, { useState, useEffect } from 'react';
import {
  FileSearch, Loader2, CheckCircle, AlertTriangle, FileText,
  ChevronDown, ChevronUp, Sparkles, ClipboardList, Target,
  AlertCircle, RefreshCw, Users, Shield, Award, Calendar,
  Lightbulb, TrendingUp, BadgeCheck, Lock, Clock, FileCheck,
  Briefcase, DollarSign, MapPin, Star
} from 'lucide-react';
import { useDocumentAnalysis, ContractRequirement, DocumentAnalysisResult } from '../hooks/useDocumentAnalysis';
import toast from 'react-hot-toast';

// The analysis AI returns inconsistent shapes for the same fields (a string in
// one contract, an array or an object in another, e.g. certifications as
// "PMP" vs { name, level, requiredFor }, qualifications as a string vs array).
// Rendering a raw object as a React child throws "Minified React error #31",
// so coerce anything to a safe string / array before rendering.
function asText(v: any): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    return (
      v.text || v.name || v.title || v.label || v.value || v.description ||
      v.requirement || v.qualification || v.event || ''
    ) || Object.values(v).filter(x => typeof x === 'string').join(' — ') || JSON.stringify(v);
  }
  return String(v);
}

function asList(v: any): any[] {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  return [v];
}

interface DocumentAnalysisPanelProps {
  contractId: string;
  hasDocuments: boolean;
  onAnalysisComplete?: (result: DocumentAnalysisResult) => void;
  /** Bump to force a re-fetch of the persisted analysis (e.g. after an import). */
  refreshKey?: number;
}

export function DocumentAnalysisPanel({
  contractId,
  hasDocuments,
  onAnalysisComplete,
  refreshKey = 0
}: DocumentAnalysisPanelProps) {
  const {
    loading,
    analyzing,
    error,
    fetchAnalysis,
    analyzeDocuments,
    getGroupedRequirements,
    getMandatoryRequirements
  } = useDocumentAnalysis();

  const [analysis, setAnalysis] = useState<DocumentAnalysisResult | null>(null);
  const [showRequirements, setShowRequirements] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'overview' | 'requirements' | 'sow' | 'personnel'>('overview');

  // Fetch existing analysis on mount
  useEffect(() => {
    async function loadAnalysis() {
      const result = await fetchAnalysis(contractId);
      setAnalysis(result);
      if (result.isAnalyzed && onAnalysisComplete) {
        onAnalysisComplete(result);
      }
    }
    loadAnalysis();
  }, [contractId, fetchAnalysis, onAnalysisComplete, refreshKey]);

  const handleAnalyze = async (force: boolean = false) => {
    toast.loading('Analyzing documents... This may take a minute.', { id: 'analyze' });

    const result = await analyzeDocuments(contractId, force);

    if (result.success) {
      toast.success(
        `Found ${result.requirementsExtracted} requirements from ${result.documentsProcessed} documents!`,
        { id: 'analyze' }
      );

      // Refresh the analysis data
      const updatedAnalysis = await fetchAnalysis(contractId);
      setAnalysis(updatedAnalysis);
      if (onAnalysisComplete) {
        onAnalysisComplete(updatedAnalysis);
      }
    } else {
      toast.error(result.error || 'Analysis failed', { id: 'analyze' });
    }
  };

  const toggleType = (type: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-urgent bg-urgent-bg border-urgent-border';
      case 'high': return 'text-fit-moderate bg-orange-50 border-orange-200';
      case 'medium': return 'text-fit-moderate bg-fit-moderate-bg border-fit-moderate-border';
      case 'low': return 'text-success bg-success-bg border-success-border';
      default: return 'text-bw-muted bg-bw-surface-subtle border-bw-border';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'technical': return <FileText className="h-4 w-4" />;
      case 'experience': return <Briefcase className="h-4 w-4" />;
      case 'certification': return <Award className="h-4 w-4" />;
      case 'staffing': return <Users className="h-4 w-4" />;
      case 'security': return <Shield className="h-4 w-4" />;
      case 'clearance': return <Lock className="h-4 w-4" />;
      case 'past_performance': return <TrendingUp className="h-4 w-4" />;
      case 'financial': return <DollarSign className="h-4 w-4" />;
      default: return <ClipboardList className="h-4 w-4" />;
    }
  };

  // Get summary data
  const summary = analysis?.summary as any;
  const keyPersonnel = summary?.keyPersonnel || [];
  const certifications = summary?.certifications || [];
  const clearances = summary?.clearances || [];
  const proposalTips = summary?.proposalTips || [];
  const criticalDates = summary?.criticalDates || [];
  const winThemes = summary?.winThemes || [];
  const pageLimits = summary?.pageLimits;
  const evaluationMethod = summary?.evaluationMethod;
  const factorRanking = summary?.factorRanking;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-bw-muted">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        Loading analysis...
      </div>
    );
  }

  // Not analyzed yet
  if (!analysis?.isAnalyzed) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 bg-bw-surface-subtle rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Sparkles className="h-8 w-8 text-bw-primary" />
        </div>
        <h3 className="font-semibold text-bw-ink mb-2">AI Document Analysis</h3>
        <p className="text-sm text-bw-muted mb-4 max-w-sm mx-auto">
          Extract requirements, key personnel, certifications, and win strategies from your uploaded documents.
        </p>
        {!hasDocuments && (
          <p className="text-sm text-fit-moderate mb-4 flex items-center justify-center gap-1">
            <AlertTriangle className="h-4 w-4" />
            Upload documents first for best results
          </p>
        )}
        <button
          onClick={() => handleAnalyze(false)}
          disabled={analyzing}
          className="inline-flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          {analyzing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <FileSearch className="h-4 w-4 mr-2" />
              Analyze Documents
            </>
          )}
        </button>
      </div>
    );
  }

  // Analysis complete - show results
  const mandatoryCount = getMandatoryRequirements(analysis.requirements).length;
  const evalFactorCount = analysis.requirements.filter(r => r.is_evaluation_factor).length;

  return (
    <div className="space-y-4">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-bg text-success">
            <CheckCircle className="h-3 w-3 mr-1" />
            Analyzed
          </span>
          <button
            onClick={() => handleAnalyze(true)}
            disabled={analyzing}
            className="text-xs text-bw-primary hover:text-bw-primary flex items-center"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${analyzing ? 'animate-spin' : ''}`} />
            Re-analyze
          </button>
        </div>
      </div>

      {/* Summary */}
      {summary?.summary && (
        <div className="p-3 bg-bw-surface-subtle rounded-lg border border-purple-100">
          <p className="text-sm text-bw-ink">{summary.summary}</p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2">
        <div className="text-center p-2 bg-bw-primary-tint rounded-lg">
          <div className="text-xl font-bold text-bw-primary">{analysis.requirementsCount}</div>
          <div className="text-xs text-bw-muted">Requirements</div>
        </div>
        <div className="text-center p-2 bg-urgent-bg rounded-lg">
          <div className="text-xl font-bold text-urgent">{mandatoryCount}</div>
          <div className="text-xs text-bw-muted">Mandatory</div>
        </div>
        <div className="text-center p-2 bg-fit-moderate-bg rounded-lg">
          <div className="text-xl font-bold text-fit-moderate">{evalFactorCount}</div>
          <div className="text-xs text-bw-muted">Eval Factors</div>
        </div>
        <div className="text-center p-2 bg-bw-primary-tint rounded-lg">
          <div className="text-xl font-bold text-bw-primary">{keyPersonnel.length}</div>
          <div className="text-xs text-bw-muted">Key Staff</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-bw-border">
        {[
          { id: 'overview', label: 'Win Strategy', icon: TrendingUp },
          { id: 'requirements', label: 'Requirements', icon: ClipboardList },
          { id: 'personnel', label: 'Key Personnel', icon: Users },
          { id: 'sow', label: 'SOW', icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-purple-600 text-bw-primary'
                : 'border-transparent text-bw-muted hover:text-bw-ink'
            }`}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[200px]">
        {/* Win Strategy Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Win Themes */}
            {winThemes.length > 0 && (
              <div className="bg-success-bg border border-success-border rounded-lg p-3">
                <h4 className="font-medium text-success mb-2 flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Win Themes
                </h4>
                <ul className="space-y-1">
                  {winThemes.map((theme: any, i: number) => (
                    <li key={i} className="text-sm text-success flex items-start gap-2">
                      <CheckCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      {asText(theme)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Proposal Tips */}
            {proposalTips.length > 0 && (
              <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-3">
                <h4 className="font-medium text-fit-moderate mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  Proposal Tips
                </h4>
                <ul className="space-y-1">
                  {proposalTips.map((tip: any, i: number) => (
                    <li key={i} className="text-sm text-fit-moderate flex items-start gap-2">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      {asText(tip)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Evaluation Method */}
            {evaluationMethod && (
              <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-3">
                <h4 className="font-medium text-bw-primary mb-2 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Evaluation Approach
                </h4>
                <p className="text-sm text-bw-primary font-medium">{asText(evaluationMethod)}</p>
                {factorRanking && (
                  <p className="text-sm text-bw-primary mt-1">{asText(factorRanking)}</p>
                )}
              </div>
            )}

            {/* Required Certifications & Clearances */}
            <div className="grid grid-cols-2 gap-3">
              {certifications.length > 0 && (
                <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-3">
                  <h4 className="font-medium text-bw-primary mb-2 flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Certifications
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {certifications.map((cert: any, i: number) => (
                      <span key={i} className="px-2 py-0.5 bg-bw-primary-tint text-bw-primary text-xs rounded-full">
                        {asText(cert)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {clearances.length > 0 && (
                <div className="bg-urgent-bg border border-urgent-border rounded-lg p-3">
                  <h4 className="font-medium text-urgent mb-2 flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Clearances Required
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {clearances.map((clearance: any, i: number) => (
                      <span key={i} className="px-2 py-0.5 bg-urgent-bg text-urgent text-xs rounded-full">
                        {asText(clearance)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Critical Dates */}
            {criticalDates.length > 0 && (
              <div className="bg-bw-surface-subtle border border-bw-border rounded-lg p-3">
                <h4 className="font-medium text-bw-ink mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Critical Dates
                </h4>
                <div className="space-y-1">
                  {criticalDates.map((date: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-bw-muted">{asText(date?.event ?? date)}</span>
                      <span className="font-medium text-bw-ink">{asText(date?.date) || 'TBD'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Page Limits */}
            {pageLimits && Object.keys(pageLimits).some(k => pageLimits[k]) && (
              <div className="bg-bw-surface-subtle border border-bw-border rounded-lg p-3">
                <h4 className="font-medium text-bw-ink mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Page Limits
                </h4>
                <div className="flex gap-4 text-sm">
                  {pageLimits.technical && <span>Technical: <strong>{asText(pageLimits.technical)}</strong></span>}
                  {pageLimits.pastPerformance && <span>PP: <strong>{asText(pageLimits.pastPerformance)}</strong></span>}
                  {pageLimits.price && <span>Price: <strong>{asText(pageLimits.price)}</strong></span>}
                  {pageLimits.total && <span>Total: <strong>{asText(pageLimits.total)}</strong></span>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Requirements Tab */}
        {activeTab === 'requirements' && (
          <div className="space-y-3">
            {Object.entries(getGroupedRequirements(analysis.requirements)).map(([type, reqs]) => (
              <div key={type} className="border border-bw-border rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleType(type)}
                  className="w-full flex items-center justify-between p-3 bg-bw-surface-subtle hover:bg-bw-surface-subtle transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {getTypeIcon(type)}
                    <span className="font-medium capitalize text-sm">{type.replace('_', ' ')}</span>
                    <span className="text-xs text-bw-muted bg-gray-200 px-1.5 py-0.5 rounded">
                      {reqs.length}
                    </span>
                  </div>
                  {expandedTypes.has(type) ? (
                    <ChevronUp className="h-4 w-4 text-bw-muted" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-bw-muted" />
                  )}
                </button>

                {expandedTypes.has(type) && (
                  <div className="divide-y divide-gray-100">
                    {reqs.map((req, idx) => (
                      <div key={req.id || idx} className="p-3 hover:bg-bw-surface-subtle">
                        <div className="flex items-start gap-2 mb-1">
                          {req.is_mandatory && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-urgent-bg text-urgent rounded">
                              MANDATORY
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded border ${getPriorityColor(req.priority)}`}>
                            {req.priority}
                          </span>
                          {req.is_evaluation_factor && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-bw-primary-tint text-bw-primary rounded">
                              EVAL FACTOR
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-bw-ink">{req.requirement_text}</p>
                        {req.source_section && (
                          <p className="text-xs text-bw-muted mt-1">Source: {req.source_section}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {analysis.requirements.length === 0 && (
              <p className="text-center text-bw-muted py-4 text-sm">
                No specific requirements extracted. Try re-analyzing with more detailed documents.
              </p>
            )}
          </div>
        )}

        {/* Key Personnel Tab */}
        {activeTab === 'personnel' && (
          <div className="space-y-3">
            {keyPersonnel.length > 0 ? (
              keyPersonnel.map((person: any, i: number) => (
                <div key={i} className="border border-bw-border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-bw-ink">{asText(person.title || person.role) || 'Personnel'}</h4>
                    {person.required && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-urgent-bg text-urgent rounded">
                        REQUIRED
                      </span>
                    )}
                  </div>
                  {person.experience && (
                    <p className="text-sm text-bw-muted mb-1">
                      <strong>Experience:</strong> {asText(person.experience)}
                    </p>
                  )}
                  {asList(person.qualifications ?? person.requirements).length > 0 && (
                    <div className="text-sm text-bw-muted mb-1">
                      <strong>Qualifications:</strong>
                      <ul className="list-disc list-inside ml-2">
                        {asList(person.qualifications ?? person.requirements).map((q: any, j: number) => (
                          <li key={j}>{asText(q)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {asList(person.certifications).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {asList(person.certifications).map((cert: any, j: number) => (
                        <span key={j} className="px-2 py-0.5 text-xs bg-bw-primary-tint text-bw-primary rounded-full">
                          {asText(cert)}
                        </span>
                      ))}
                    </div>
                  )}
                  {person.clearance && (
                    <p className="text-sm text-urgent mt-2">
                      <Lock className="h-3 w-3 inline mr-1" />
                      {asText(person.clearance)}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-bw-muted py-4 text-sm">
                No specific key personnel requirements found.
              </p>
            )}
          </div>
        )}

        {/* SOW Tab */}
        {activeTab === 'sow' && (
          <div className="space-y-3">
            {analysis.sow ? (
              <>
                {analysis.sow.scope && (
                  <div className="p-3 bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
                    <h4 className="font-medium text-bw-primary mb-1">Scope</h4>
                    <p className="text-sm text-bw-primary">{asText(analysis.sow.scope)}</p>
                  </div>
                )}
                {analysis.sow.background && (
                  <div className="p-3 bg-bw-surface-subtle border border-bw-border rounded-lg">
                    <h4 className="font-medium text-bw-ink mb-1">Background</h4>
                    <p className="text-sm text-bw-ink">{asText(analysis.sow.background)}</p>
                  </div>
                )}
                {asList(analysis.sow.objectives).length > 0 && (
                  <div className="p-3 bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
                    <h4 className="font-medium text-bw-primary mb-1">Objectives</h4>
                    <ul className="list-disc list-inside text-sm text-bw-primary">
                      {asList(analysis.sow.objectives).map((obj: any, i: number) => (
                        <li key={i}>{asText(obj)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.sow.period_of_performance && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-bw-muted" />
                    <span className="text-bw-muted">Period:</span>
                    <span className="font-medium">{asText(analysis.sow.period_of_performance)}</span>
                  </div>
                )}
                {analysis.sow.place_of_performance && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-bw-muted" />
                    <span className="text-bw-muted">Location:</span>
                    <span className="font-medium">{asText(analysis.sow.place_of_performance)}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-bw-muted py-4 text-sm">
                No Statement of Work details extracted.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="p-3 bg-urgent-bg border border-urgent-border rounded-lg text-urgent text-sm flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

    </div>
  );
}
